import { Fragment } from "react";
import type { EnvMatrix, EnvMatrixCell, EnvMatrixRow, EnvMatrixService } from "@/lib/api";
import { compareAcross, rowsForTable } from "@/lib/envMatrix";
import { copyText } from "@/lib/clipboard";
import { TINT } from "./tints";
import { useHoverReveal, useSecretForCopy } from "./reveal";

export interface CellRef {
  serviceId: string;
  key: string;
  kind: EnvMatrixRow["kind"];
}

/** A row of the table: one key. */
export type RowRef = Pick<CellRef, "key" | "kind">;

const rowId = (r: EnvMatrixRow) => `${r.kind}:${r.key}`;

/** The cell's element id, so "Show in table" can scroll to it. */
export const cellDomId = (at: CellRef) =>
  `env-cell-${at.kind}-${at.serviceId}-${at.key}`.replace(/[^A-Za-z0-9_-]/g, "_");

/**
 * One kind of service at a time, its environments side by side (App: Demo,
 * Dev), so like is compared with like; the page's chips pick which. It lists
 * only the keys its services use, each with a mark saying whether those
 * services agree on it. Click a key for its details; click a value to copy it.
 */
export function MatrixTable({
  m,
  tableName,
  rows,
  selected,
  onOpenKey,
  onNotify,
}: {
  m: EnvMatrix;
  tableName: string;
  rows: EnvMatrixRow[];
  selected: CellRef | null;
  onOpenKey: (row: RowRef) => void;
  onNotify: (message: string) => void;
}) {
  const table = m.tables.find((t) => t.name === tableName);
  if (!table) return null;
  const tableRows = rowsForTable(rows, table.serviceIds);

  if (tableRows.length === 0) {
    return (
      <div className="rounded-token-lg border border-border bg-bg-card px-4 py-8 text-sm text-text-muted">
        No keys in {table.name} match. Clear the filters to see them all.
      </div>
    );
  }
  return (
    <ServiceTable
      m={m}
      name={table.name}
      serviceIds={table.serviceIds}
      rows={tableRows}
      selected={selected}
      onOpenKey={onOpenKey}
      onNotify={onNotify}
    />
  );
}

function ServiceTable({
  m,
  name,
  serviceIds,
  rows,
  selected,
  onOpenKey,
  onNotify,
}: {
  m: EnvMatrix;
  name: string;
  serviceIds: string[];
  rows: EnvMatrixRow[];
  selected: CellRef | null;
  onOpenKey: (row: RowRef) => void;
  onNotify: (message: string) => void;
}) {
  const byId = new Map(m.services.map((s) => [s.id, s]));
  const headingOf = (r: EnvMatrixRow) => r.groups[name] ?? r.group;
  const groups = m.keyGroups.filter((g) => rows.some((r) => headingOf(r) === g));

  return (
    <section className="min-w-0 overflow-x-auto rounded-token-lg border border-border bg-bg-card">
      {/* The key column holds its width; the services share the rest, so a
          long value gets the room the page has. Below a minimum, scroll. */}
      <table
        className="w-full table-fixed border-separate border-spacing-0 text-[13px]"
        style={{ minWidth: `${16 + serviceIds.length * 13}rem` }}
      >
        <colgroup>
          <col style={{ width: "16rem" }} />
          {serviceIds.map((id) => (
            <col key={id} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th
              scope="col"
              className="sticky left-0 z-20 w-64 min-w-[16rem] border-b border-r border-border bg-bg-card px-3 pb-2.5 pt-3 text-left align-bottom font-normal"
            >
              <span className="block font-display text-base font-semibold text-text-heading">{name}</span>
              <span className="block text-[11px] text-text-muted">
                {rows.length} {rows.length === 1 ? "key" : "keys"}
              </span>
            </th>
            {serviceIds.map((id) => {
              const s = byId.get(id)!;
              return (
                <th
                  key={id}
                  scope="col"
                  className="border-b border-border px-2.5 pb-2.5 pt-3 text-left align-bottom font-normal"
                >
                  <span className="block text-sm font-semibold text-text-heading">{columnLabel(s)}</span>
                  <span className="block font-mono text-[11px] text-text-muted">
                    {s.name}
                    {s.suspended && " · suspended"}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <Fragment key={g}>
              <tr>
                <th
                  scope="rowgroup"
                  className="sticky left-0 z-10 border-b border-r border-border bg-bg-secondary px-3 pb-1 pt-2 text-left text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted"
                >
                  {g}
                </th>
                <td colSpan={serviceIds.length} className="border-b border-border bg-bg-secondary" />
              </tr>
              {rows
                .filter((r) => headingOf(r) === g)
                .map((r) => (
                  <tr key={rowId(r)}>
                    {/* The whole box opens the key's details, not just its text.
                        The name stays a button for the keyboard: Enter on it
                        clicks, and the click reaches this cell. */}
                    <th
                      scope="row"
                      onClick={() => onOpenKey({ key: r.key, kind: r.kind })}
                      title={`Compare ${r.key} across ${name}`}
                      className="group sticky left-0 z-10 w-64 min-w-[16rem] max-w-[16rem] cursor-pointer border-b border-r border-border-light bg-bg-card px-3 py-1 text-left font-normal transition-colors hover:bg-bg-card-hover"
                    >
                      <span className="flex items-center gap-1.5">
                        <button
                          type="button"
                          className="break-all text-left font-mono text-xs font-medium text-text-heading group-hover:text-accent group-hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
                        >
                          {r.key}
                        </button>
                        {r.kind === "file" && <span className="text-[10.5px] text-text-muted">file</span>}
                        <CompareMark row={r} services={serviceIds.map((id) => byId.get(id)!)} />
                      </span>
                    </th>
                    {serviceIds.map((id) => {
                      const isSel =
                        selected?.serviceId === id && selected.key === r.key && selected.kind === r.kind;
                      return (
                        <td key={id} className="border-b border-border-light p-0.5">
                          <CellButton
                            domId={cellDomId({ serviceId: id, key: r.key, kind: r.kind })}
                            cell={r.cells[id]}
                            serviceId={id}
                            row={r}
                            serviceName={byId.get(id)!.name}
                            selected={isSel}
                            onNotify={onNotify}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** "Demo", "Dev"…; a service with no environment shows its type instead. */
export const columnLabel = (s: EnvMatrixService) => (s.environment !== "Other" ? s.environment : s.type);

/**
 * = when every service in the table has the same value, ≠ when they don't —
 * secrets included, compared on the server without being shown. Hover for
 * which services differ.
 */
export function CompareMark({
  row,
  services,
  withText = false,
}: {
  row: EnvMatrixRow;
  services: EnvMatrixService[];
  /** Spell the verdict out beside the symbol, for the details modal. */
  withText?: boolean;
}) {
  const c = compareAcross(
    row,
    services.map((s) => s.id),
  );
  if (!c) return null;
  const nameOf = (id: string) => services.find((s) => s.id === id)!.name;
  const label =
    c.state === "match"
      ? `Same value on ${services.length === 2 ? "both services" : `all ${services.length} services`}`
      : c.missing.length > 0
        ? `Not set on ${c.missing.map(nameOf).join(", ")}`
        : "The values differ";
  const mark = (
    <span
      title={withText ? undefined : label}
      aria-label={withText ? undefined : label}
      className={`grid h-5 w-5 shrink-0 place-items-center rounded font-mono text-[13px] font-semibold leading-none ${
        withText ? "" : "ml-auto"
      } ${c.state === "match" ? "text-text-muted" : `text-warning ${TINT.warning}`}`}
    >
      {c.state === "match" ? "=" : "≠"}
    </span>
  );
  if (!withText) return mark;
  return (
    <span className="inline-flex items-center gap-2 text-text">
      {mark}
      {label}
    </span>
  );
}

/** ✓ when a rule checking this key passes, ! when it's broken. */
export function RuleMark({ ok, title }: { ok: boolean; title?: string }) {
  return ok ? (
    <span
      title={title ?? "This rule passes"}
      className={`grid h-4 w-4 shrink-0 place-items-center rounded-full text-[9px] text-success ${TINT.success}`}
    >
      <i className="fa-solid fa-check" />
    </span>
  ) : (
    <span
      title={title ?? "This rule is broken"}
      className="grid h-4 w-4 shrink-0 place-items-center rounded-full bg-danger text-[9px] text-white"
    >
      <i className="fa-solid fa-exclamation" />
    </span>
  );
}

/** A value: hover a secret to see it, click any value to copy it. */
function CellButton({
  domId,
  cell,
  serviceId,
  row,
  serviceName,
  selected,
  onNotify,
}: {
  domId: string;
  cell: EnvMatrixCell | undefined;
  serviceId: string;
  row: EnvMatrixRow;
  serviceName: string;
  selected: boolean;
  onNotify: (message: string) => void;
}) {
  const isSecret = !!(cell?.set && cell.secret);
  const target = isSecret
    ? { serviceId, key: row.key, fingerprint: cell!.fingerprint, revealable: cell!.revealable }
    : null;
  const { state, bind } = useHoverReveal(row.kind === "var" ? target : null);
  const secretForCopy = useSecretForCopy();
  const label = `${row.key} on ${serviceName}`;

  async function copy() {
    if (!cell?.set) {
      onNotify(`${label} isn't set, so there's nothing to copy.`);
      return;
    }
    let text: string | Promise<string>;
    if (row.kind === "file") {
      onNotify("Secret files can't be copied from here.");
      return;
    } else if (!cell.secret) {
      text = cell.value ?? "";
    } else if (state.status === "shown") {
      text = state.value;
    } else {
      const got = secretForCopy(target!);
      if (!got.ok) {
        onNotify(got.reason);
        return;
      }
      text = got.value;
    }
    try {
      await copyText(text);
      onNotify(`Copied ${label}${cell.secret ? ". The reveal is noted in the audit log" : ""}.`);
    } catch (e) {
      onNotify(`Couldn't copy ${label}: ${(e as Error).message}`);
    }
  }

  let inner: React.ReactNode;
  let described: string;
  // A hovered secret shows in the cell itself, cut to fit: clicking copies
  // the whole value, so no popover is needed.
  if (isSecret && state.status === "shown") {
    inner = <span className="min-w-0 flex-1 truncate font-mono text-xs text-accent">{state.value}</span>;
    described = "secret, revealed";
  } else if (isSecret && state.status === "unavailable") {
    const why = state.reason.startsWith("Couldn't")
      ? "couldn't reveal"
      : cell!.revealable
        ? "admins only"
        : "not shown";
    inner = (
      <span className="min-w-0 truncate text-xs text-text-muted">
        <i className="fa-solid fa-lock mr-1.5 text-[10px]" />
        {why}
      </span>
    );
    described = state.reason;
  } else if (cell?.set && cell.secret) {
    inner = (
      <span aria-hidden className="inline-flex items-center gap-1.5 font-mono text-xs tracking-widest text-text-muted">
        ••••••
        {state.status === "loading" && <i className="fa-solid fa-circle-notch fa-spin text-[10px] tracking-normal" />}
      </span>
    );
    described = "secret, hover to reveal";
  } else if (cell?.set) {
    inner = <span className="min-w-0 flex-1 truncate font-mono text-xs text-text">{cell.value}</span>;
    described = cell.value ?? "";
  } else if (cell?.flag === "missing") {
    inner = (
      <span className={`rounded px-1.5 py-0.5 font-mono text-[11px] font-medium text-warning ${TINT.warning}`}>
        missing
      </span>
    );
    described = "missing, a rule expects it";
  } else {
    inner = (
      <span aria-hidden className="pl-1 text-base leading-none text-text-muted opacity-40">
        ·
      </span>
    );
    described = cell ? "declared in render.yaml, not set" : "not set";
  }

  const tag = !cell?.set
    ? null
    : cell.differsFromYaml
      ? { text: "edited", cls: "border-dashed border-warning text-warning" }
      : cell.source === "yaml"
        ? { text: "yaml", cls: "border-border text-text-muted" }
        : cell.source === "untracked"
          ? { text: "not in yaml", cls: "border-dashed border-warning text-warning" }
          : cell.source === "group"
            ? { text: "group", cls: "border-border text-text-muted" }
            : null;

  const tone =
    cell?.flag === "bad"
      ? `border-danger ${TINT.danger}`
      : cell?.flag === "missing"
        ? "border-dashed border-warning"
        : "border-transparent";

  return (
    <button
      id={domId}
      type="button"
      {...bind}
      onClick={copy}
      aria-label={`Copy ${label}: ${described}`}
      aria-pressed={selected}
      // No tooltip on a secret: nothing should float over the table when one is hovered.
      title={isSecret ? undefined : cell?.set ? `${cell.value ?? ""}\nClick to copy` : label}
      className={`flex h-8 w-full min-w-0 items-center gap-1.5 rounded-token-sm border px-2 text-left transition-colors hover:bg-bg-card-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent ${tone} ${selected ? "ring-2 ring-accent" : ""}`}
    >
      {inner}
      {tag && (
        <span className={`ml-auto shrink-0 rounded border px-1 py-px font-mono text-[10px] leading-tight ${tag.cls}`}>
          {tag.text}
        </span>
      )}
    </button>
  );
}
